import axios from "axios";
import i18n from "i18next";
import * as Sentry from "@sentry/react";

import { config } from "../../config";
import { store } from "../store";
import { setAppKey, logout } from "../slices/auth.slice";
import { controlLoading } from "../slices/app.slice";
import {
  isExpectedApiStatus,
  reportError,
  sanitizeEndpoint,
} from "../config/reportError";

/**
 * One forced business sign-out per page life: a burst of parallel calls can
 * all receive -40022, but only the first may log out and navigate (a full
 * page load resets this flag anyway).
 *
 * -40022 is returned by `resolveBusinessAccount` when the account row is
 * missing or `users.active !== true`. Expired/invalid *tokens* never reach
 * it: `accessTokenChecking` answers them with envelope statuses 3/6 first,
 * so normal sessions are not logged out by this handler.
 */
let businessSessionExpiredHandled = false;

const handleBusinessSessionExpired = () => {
  if (businessSessionExpiredHandled) return;
  businessSessionExpiredHandled = true;
  store.dispatch(logout());
  // redux-persist holds the session in localStorage under "persist:root";
  // drop it synchronously so the redirect cannot rehydrate a valid-looking
  // session if the persistor's async write never lands.
  try {
    window.localStorage.removeItem("persist:root");
  } catch (err) {
    // Storage may be unavailable; the logout dispatch still clears memory.
  }
  // service.ts cannot use useNavigate, so do a full navigation.
  window.location.assign("/business/login");
};

let appKeyPromise: Promise<string | null> | null = null;

const MAX_DEVICE_FIELD_LENGTH = 191;
const clipDeviceField = (value: string): string =>
  value.slice(0, MAX_DEVICE_FIELD_LENGTH);

/**
 * Axios client mirroring the RN app's `service.ts` protocol:
 * - headers: lang / apikey / appkey / accesstoken
 * - GET params, POST/PUT body
 * - envelope status 3/4/5/6 -> alert + console.error (no logout)
 * - network error -> `{ code: "0", success: false, msg: "network error" }`
 */
export const call = async (
  _method?: string,
  _endpoint?: string,
  _postObj?: any,
  _opts?: any,
): Promise<any> => {
  const opts = _opts || {};
  const skipAppKey = opts.skipAppKey === true;
  let app_key: string | null = store.getState().auth.app_key || config.APP_KEY;
  const access_token = store.getState().auth.access_token;

  // The app key is created asynchronously at boot. Any request that wins that
  // race would otherwise be rejected by the backend with status 5 ("no app
  // key") — the warning/alerts seen in the web console. Await the single
  // shared bootstrap instead; /device itself opts out to avoid recursion.
  if (!skipAppKey && !app_key) {
    app_key = await createAppKey();
    if (!app_key) {
      return {
        code: "0",
        success: false,
        status: 5,
        resp: "Unable to initialise secure session. Please reload and try again.",
        result: "Unable to initialise secure session. Please reload and try again.",
        msg: "app key unavailable",
      };
    }
  }

  const startedAt = Date.now();
  const endpointPath = sanitizeEndpoint(_endpoint);
  let httpStatus: number | undefined;
  let apiStatus: number | string | undefined;

  try {
    const baseURL = config.API_URL;
    const method = _method?.toLowerCase();
    const endpoint = _endpoint;
    const postObj = _postObj || {};
    let params;
    const headers = {
      lang: i18n.language,
      apikey: config.API_KEY,
      appkey: app_key ? app_key : config.APP_KEY,
      accesstoken: access_token ? access_token : config.ACCESS_TOKEN,
    };
    if (method === "get") {
      params = postObj;
    }

    const resp = await axios({
      method,
      baseURL,
      url: endpoint,
      headers,
      timeout: opts?.timeout || 5000,
      params, // GET
      data: method === "get" ? null : postObj, // POST/PUT/PATCH
      withCredentials: true,
    });
    const respData = resp.data;
    const { status } = respData;
    httpStatus = resp.status;
    apiStatus = status;

    if (status === 3 || status === 4 || status === 5 || status === 6) {
      window.alert(respData.resp);
      console.error(respData.resp);
      // Expected auth/key failures: one warning-level event, not an error
      // issue. Return the envelope so callers keep their normal protocol.
      reportError(
        new Error(
          respData?.resp || `API request rejected with status ${status}`,
        ),
        {
          area: "api",
          operation: "service.call",
          method: _method,
          endpoint: _endpoint,
          httpStatus: resp.status,
          apiStatus: status,
          level: "warning",
        },
      );
      return respData;
    }
    // A deactivated business (-40022 from resolveBusinessAccount) is signed
    // out immediately and sent back to the business login page.
    if (status === -40022 && store.getState().auth?.user_auth === "business") {
      handleBusinessSessionExpired();
      return respData;
    }
    return respData;
  } catch (err: any) {
    httpStatus = err?.response?.status;
    apiStatus = err?.response?.data?.status;
    if (err.response) {
      console.error(err.response);
    } else if (err.request) {
      console.error(err.request);
    } else {
      console.error(err.message);
    }
    console.error(err.config);
    // Exactly one event per failed request. The reporter normalises Axios
    // errors and only receives safe metadata — never err.config/request data.
    reportError(err, {
      area: "api",
      operation: "service.call",
      method: _method,
      endpoint: _endpoint,
      httpStatus,
      apiStatus,
    });
    setTimeout(() => {
      store.dispatch(controlLoading({ showLoading: false }));
    }, 500);
  } finally {
    try {
      Sentry.addBreadcrumb({
        category: "api",
        level: isExpectedApiStatus(apiStatus) ? "warning" : "info",
        message: `${(_method || "GET").toUpperCase()} ${endpointPath ?? "unknown"}`,
        data: {
          method: _method?.toUpperCase(),
          endpoint: endpointPath,
          httpStatus,
          apiStatus,
          durationMs: Date.now() - startedAt,
        },
      });
    } catch {
      // Breadcrumbs are diagnostics only; never break the request flow.
    }
  }
  return {
    code: "0",
    success: false,
    msg: "network error",
  };
};

/**
 * Create an app key via the backend `/device` endpoint (apikey-only route).
 * The backend generates `app_key = uuidv4().replace(/-/g, "")` and stores it
 * in `device_infos`; any non-empty appkey header is accepted by `checking.js`.
 */
export const createAppKey = async (): Promise<string | null> => {
  const app_key = store.getState().auth.app_key || config.APP_KEY;
  if (app_key) {
    config.APP_KEY = app_key;
    return app_key;
  }

  // Concurrent boot/API calls must share one /device request instead of
  // creating multiple device rows and racing each other's app_key.
  if (appKeyPromise) return appKeyPromise;

  appKeyPromise = (async (): Promise<string | null> => {
    const userAgent = navigator.userAgent || "unknown";
    const deviceInfoObj = {
      os: clipDeviceField(navigator.platform || "unknown"),
      os_version: clipDeviceField(userAgent),
      unique_id: `web_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
      supported_abi: "web",
      screen_height: Math.floor(window.screen.height),
      screen_width: Math.floor(window.screen.width),
      brand: "web",
      model: "browser",
    };

    const result = await call("POST", "/device", deviceInfoObj, {
      timeout: 15000,
      skipAppKey: true,
    });

    if (result?.app_key) {
      store.dispatch(setAppKey({ app_key: result.app_key }));
      config.APP_KEY = result.app_key;
      return result.app_key;
    }

    return null;
  })();

  try {
    return await appKeyPromise;
  } finally {
    appKeyPromise = null;
  }
};
